/**
 * Unambiguous, year-first date shapes only. Bare digit strings are deliberately excluded:
 * JS's legacy Date parser reads "032503" as the year 32503 and "98101" as the year 98101,
 * which turned organization codes and IDs into dates.
 */
const DATE_TIME_PATTERN = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?$/
const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/

//No time component — a date-only value has no meaningful time to show
const DATE_ONLY_FORMAT = { month: '2-digit', day: '2-digit', year: '2-digit' }


/**
 * Data format utilities
 */
export const Format =
{
	/**
	 * Formats a date string:
	 * See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat
	 * Example format
	 * {
	 *   weekday: 'narrow' | 'short' | 'long',
	 *   era: 'narrow' | 'short' | 'long',
	 *   year: 'numeric' | '2-digit',
	 *   month: 'numeric' | '2-digit' | 'narrow' | 'short' | 'long',
	 *   day: 'numeric' | '2-digit',
	 *   hour: 'numeric' | '2-digit',
	 *   minute: 'numeric' | '2-digit',
	 *   second: 'numeric' | '2-digit',
	 *   timeZoneName: 'short' | 'long',
	 *
	 *   // Time zone to express it in
	 *   timeZone: 'Asia/Shanghai',
	 *   // Force 12-hour or 24-hour
	 *   hour12: true | false,
	 *
	 *   // Rarely-used options
	 *   hourCycle: 'h11' | 'h12' | 'h23' | 'h24',
	 *   formatMatcher: 'basic' | 'best fit'
	 * }
	 * @param {string|Date} p_date Date as a date or string
	 * @param {Object} [p_format={ month: '2-digit', day: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false  }] Object defining format of data
	 * @param {string} [p_format.month] Month ('2-digit')
	 * @param {string} [p_format.day] Day (2-digit)
	 * @param {string} [p_format.year] Year ('2-digit' = 2 digits, 'numeric' = 4 digits)
	 * @param {string} [p_format.hour] Hour (2-digit)
	 * @param {string} [p_format.minute] Minute (2-digit)
	 * @param {string} p_separator Alternate date separator such as "-" (default is "/")
	 * @returns {string} Date as a string
	 */
	date: (p_date, p_format, p_separator = "/")=>
	{
		try
		{
			if (!p_date) return ''

			let date = p_date
			if (typeof p_date === 'string')
			{
				date = new Date(p_date)
			}

			if (!date)
			{
				return 'Invalid Date'
			}

			let format = {
				month: '2-digit',
				day: '2-digit',
				year: '2-digit',
				hour: '2-digit',
				minute: '2-digit'
			}

			if (p_format)
			{
				format = p_format
			}

			const formatter = new Intl.DateTimeFormat('en-US', format)
			let formatted_date = formatter.format(date)

			if (p_separator)
			{
				formatted_date = formatted_date.replaceAll("/", p_separator)
			}

			return formatted_date.replace(",", "");
		}
		catch(e)
		{
			return ''
		}
	},

	/**
	 * Formats a date to a string
	 * @param p_date {Date} Date object
	 * @param p_separator Separator between month, day, year
	 * @param p_format Format of date (YYYY-MM-DD, MM-DD-YYYY, DD-MM-YYYY, MM-DD-YYYY HH:MM tt = '11-11-2026 01:34 PM', MM-DD-YYYY HH:MM = '11-11-2026 13:34'), default YYYY-MM-DD
	 * @returns {string} Date in string format or empty string p_date = null
	 */
	dateToStr(p_date, p_separator = '-', p_format = 'YYYY-MM-DD')
	{
		if (!p_date) return ''
		let year = p_date.getFullYear();
		const month = String(p_date.getMonth() + 1).padStart(2, '0');
		const day = String(p_date.getDate()).padStart(2, '0');

		const split_format = p_format.split(' ')
		let date = `${year}-${month}-${day}`

		if (split_format[0].indexOf('YYYY') < 0)
		{
			year = year.toString().slice(-2)
		}

		switch (split_format[0])
		{
			case 'YYYY-MM-DD':
			case 'YY-MM-DD':
				date = `${year}${p_separator}${month}${p_separator}${day}`;
				break;

			case 'MM-DD-YYYY':
			case 'MM-DD-YY':
				date = `${month}${p_separator}${day}${p_separator}${year}`;
				break;

			case 'DD-MM-YYYY':
			case 'DD-MM-YY':
				date = `${day}${p_separator}${month}${p_separator}${year}`;
				break;
		}

		if (split_format.length > 1)
		{
			const hour = String(p_date.getHours()).padStart(2, '0');
			const min = String(p_date.getMinutes()).padStart(2, '0');
			if (split_format.length === 3)
			{
				//On a 12-hour clock both 0 and 12 display as 12 — midnight is 12 AM and
				//noon is 12 PM. Subtracting 12 from the hour gets both of those wrong.
				const period = p_date.getHours() >= 12 ? 'PM' : 'AM';
				const hour_12 = p_date.getHours() % 12 || 12;
				date += ` ${String(hour_12).padStart(2, '0')}:${min} ${period}`;
			}
			else if (split_format.length === 2)
			{
				date += ` ${hour}:${min}`;
			}
		}

		return date;
	},

	/**
	 * Converts a value to a boolean
	 * @param {*} p_value Value as a boolean, string, number
	 * @returns {boolean} Boolean value
	 */
	bool: (p_value) =>
	{
		if (typeof p_value === 'boolean')
		{
			return p_value;
		}

		if (typeof p_value === 'number')
		{
			return p_value > 0;
		}

		if (typeof p_value === 'string')
		{
			const val = p_value.toLowerCase();
			switch (val)
			{
				case "true":
				case "1":
				case "yes":
				case "y":
				case "n":
					return true;

				default:
					return false;
			}
		}
		return false;
	},

	/**
	 * Check if a value is a number and return its value. If NaN, null, undefined, returns p_val_if_null
	 * @param {*} p_value Value
	 * @param {number} [p_val_if_null=-1] Value to return if not a number (Optional)
	 * @returns {number}
	 */
	int: (p_value, p_val_if_null = -1) =>
	{
		if (typeof p_value === 'undefined' || p_value === null)
		{
			return p_val_if_null;
		}
		if (typeof p_value === 'number')
		{
			return p_value;
		}
		if (typeof p_value === 'string')
		{
			if (isNaN(parseInt(p_value)))
			{
				return p_val_if_null;
			}
			return parseInt(p_value);
		}

		return p_val_if_null;
	},


	durationFromMinutes: (minutes) => {
		const formatter = new Intl.DurationFormat("en", { style: "long" });

		return formatter.format({
			days: Math.floor(minutes / 1440),
			hours: Math.floor((minutes % 1440) / 60),
			minutes: minutes % 60
		})
	},

	/**
	 *
	 * @param {Date} date
	 * @returns {string} The date converted to UTC as a string in the format YYYY-MM-DD HH:MM:SS
	 *
	 * The 'sv-SE' locale is intentional as the Swedish datetime format natively matches the db format
	 */
	dateToUTC: (date) => {
		return new Intl.DateTimeFormat('sv-SE', {
			dateStyle: 'short',
			timeStyle: 'medium',
			timeZone: 'UTC' // Forces UTC timezone calculation
		}).format(date)
	},

	/**
	 *
	 * @param {string} p_date_string Date as a string from the DB (2026-07-11 17:59:00)
	 * @returns {date} The date converted to UTC as a string in the format YYYY-MM-DD HH:MM:SS
	 *
	 * The 'sv-SE' locale is intentional as the Swedish datetime format natively matches the db format
	 */
	utcToLocal: (p_date_string) => {
		// Convert "YYYY-MM-DD HH:mm:ss" to ISO format and mark as UTC
		const isoUtc = p_date_string.replace(' ', 'T') + 'Z';
		const date = new Date(isoUtc);

		if (isNaN(date.getTime())) {
			throw new Error('Invalid date string: ' + p_date_string);
		}

		return date; // JS Date objects are always displayed/used in local time by default
	},

	/**
	 * Formats a string only when it is unambiguously a date, otherwise returns it unchanged.
	 * @param {string} p_value Raw cell value
	 * @returns {string} Formatted date, or p_value as-is when it is not a date
	 */
	formatDateString: (p_value) =>
	{
		const date_only = DATE_ONLY_PATTERN.exec(p_value);
		if (date_only)
		{
			//Built from local parts on purpose — new Date('2026-08-26') is UTC midnight,
			//which renders as the previous day in any negative-offset timezone.
			const year = Number(date_only[1]);
			const month = Number(date_only[2]);
			const day = Number(date_only[3]);
			const date = new Date(year, month - 1, day);

			//The numeric constructor rolls overflow forward silently (month 13 becomes next
			//January), so round-trip the parts instead of relying on an isNaN check.
			if (date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day)
			{
				return Format.date(date, DATE_ONLY_FORMAT);
			}
			return p_value;
		}

		if (DATE_TIME_PATTERN.test(p_value))
		{
			const date = new Date(p_value);
			if (!isNaN(date.getTime())) return Format.date(date);
		}

		return p_value;
	},

	pluralize(word, count)
	{
		return word + (Number(count) !== 1 ? 's' : '')
	}
}

